import React, { useEffect, useState } from "react";
import { CalendarEventTypesEnum } from "./Calendar";
import { Modal, Spin, Tag } from "antd";
import { svg } from "../../../Assets/svg";
import { useAppDispatch } from "../../../store";
import { getCalendarEventDetailsAPI } from "../TeamMembers/teamMembersApis";
import dayjs from "dayjs";
import { formatDateWithBusinessTZ, formatTimeOffDuration } from "../../../helpers/moment";
import { getFullAwsUrl } from "../../../helpers/utils";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";

interface CalendarEventDetailsModalParams {
  id: string;
  eventType: CalendarEventTypesEnum;
  open: boolean;
  onClose: () => void;
}

const timeTypeLabels = {
  1: "Morning",
  2: "Afternoon",
  3: "Lunchtime",
  4: "End of Day",
};

const CalendarEventDetailsModal: React.FC<CalendarEventDetailsModalParams> = ({
  id,
  open,
  onClose,
  eventType,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [eventDetails, setEventDetails] = useState<any>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewFiles, setPreviewFiles] = useState<any[]>([]);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);

  const fetchEventDetails = () => {
    setLoading(true);
    dispatch(
      getCalendarEventDetailsAPI({
        filters: { id: id, type: eventType },
        onSuccess: (data) => {
          setEventDetails(data);
          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  useEffect(() => {
    if (open && id) {
      fetchEventDetails();
    } else {
      setEventDetails(null);
    }
  }, [open, id]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spin size="large" />
        </div>
      );
    }

    if (!eventDetails) return null;

    if (eventType === CalendarEventTypesEnum.HOLIDAY) {
      return (
        <div 
          className="d-flex flex-column gap-3 fs-5 px-3 py-2" 
          style={{ maxHeight: "60vh", overflowY: "auto" }}
        >
          <div className="row">
            <div className="col-12 col-sm-3 fw-bold text-secondary">Name:</div>
            <div className="col-12 col-sm-9">{eventDetails.name || "-"}</div>
          </div>

          <div className="row">
            <div className="col-12 col-sm-3 fw-bold text-secondary">Date:</div>
            <div className="col-12 col-sm-9">
              {eventDetails.date
                ? formatDateWithBusinessTZ(eventDetails.date, "DD-MM-YYYY")
                : "-"}
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-sm-3 fw-bold text-secondary">Type:</div>
            <div className="col-12 col-sm-9 text-capitalize">
              {eventDetails.type ? `${eventDetails.type} Holiday` : "-"}
            </div>
          </div>
        </div>
      );
    }

    let statusColor = "default";
    if (eventDetails.status === "approved") statusColor = "success";
    else if (eventDetails.status === "rejected") statusColor = "danger";
    else if (eventDetails.status === "pending") statusColor = "warning";

    return (
      <div 
        className="d-flex flex-column gap-3 fs-5 px-3 py-2" 
        style={{ maxHeight: "60vh", overflowY: "auto" }}
      >
        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Type:</div>
          <div className="col-12 col-sm-9">{eventDetails.name || "-"}</div>
        </div>

        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Status:</div>
          <div className="col-12 col-sm-9">
            <span className={`text-${statusColor} text-capitalize`}>
              {eventDetails.status || "Pending"}
            </span>
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Date:</div>
          <div className="col-12 col-sm-9">
            {eventDetails.start ? (
              <span>
                {formatDateWithBusinessTZ(eventDetails.start, "DD-MM-YYYY")}
              </span>
            ) : "-"}
          </div>
        </div>


        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Duration:</div>
          <div className="col-12 col-sm-9">
            {formatTimeOffDuration({
              leave_type: eventDetails.leave_type,
              duration_minutes: eventDetails.duration_minutes,
              starting: { date: eventDetails.start, time_type: eventDetails.start_time_type },
              ending: { date: eventDetails.end, time_type: eventDetails.end_time_type },
            })}
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Reason:</div>
          <div className="col-12 col-sm-9" style={{ whiteSpace: "pre-wrap" }}>
            {eventDetails.note || "-"}
          </div>
        </div>

        <div className="row">
          <div className="col-12 col-sm-3 fw-bold text-secondary">Attachments:</div>
          <div className="col-12 col-sm-9">
            {eventDetails.attachments && eventDetails.attachments.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {eventDetails.attachments.map((att: any, index: number) => {
                  const rawName = att.attachment.split("/").pop() || "Attachment";
                  const cleanName = /^[a-f0-9-]{36}_/.test(rawName)
                    ? rawName.substring(37)
                    : rawName;

                  return (
                    <Tag
                      key={index}
                      className="d-inline-flex align-items-center px-3 py-1 bg-white border rounded-2 fs-6 fw-semibold cursor-pointer m-0"
                      style={{
                        border: "1px solid #E0E0E0",
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                        maxWidth: "100%",
                        height: "auto",
                      }}
                      onClick={() => {
                        setPreviewFiles(
                          eventDetails.attachments.map((a: any) => {
                            const rName = a.attachment.split("/").pop() || "Attachment";
                            const cName = /^[a-f0-9-]{36}_/.test(rName)
                              ? rName.substring(37)
                              : rName;
                            return {
                              url: getFullAwsUrl(a.attachment),
                              name: cName,
                            };
                          }),
                        );
                        setActivePreviewIndex(index);
                        setPreviewModalVisible(true);
                      }}
                    >
                      <span title="Click to view file">{cleanName}</span>
                    </Tag>
                  );
                })}
              </div>
            ) : (
              "-"
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        title={
          <div className="text-center fs-4 fw-bolder">Event Details</div>
        }
        visible={open}
        onCancel={onClose}
        width={"60rem"}
        closeIcon={svg?.closeIcon}
        centered
        footer={
          <div className="d-flex justify-content-center gap-3">
            <button
              type="button"
              className="btn btn-primary px-5"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        }
        className="custom-modal"
        destroyOnClose
      >
        {renderContent()}
      </Modal>

      {previewModalVisible && (
        <CommonFilePreviewModal
          visible={previewModalVisible}
          onClose={() => setPreviewModalVisible(false)}
          files={previewFiles}
          activeIndex={activePreviewIndex}
          onActiveIndexChange={setActivePreviewIndex}
        />
      )}
    </>
  );
};

export default CalendarEventDetailsModal;
